import React from "react";
import { motion } from "framer-motion";

export default function Vision() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <main className="bg-slate-900 text-white min-h-screen pt-32 pb-20 overflow-hidden selection:bg-aseBlue selection:text-white relative">
      {/* Background glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-aseBlue/10 rounded-full blur-[150px] pointer-events-none"></div>
      
      <div className="max-w-5xl mx-auto px-4 relative z-10 space-y-16">
        
        {/* CEO Quote Section */}
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl flex flex-col md:flex-row items-center gap-10 overflow-hidden"
        >
          {/* Subtle gradient overlay */}
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-white/5 via-transparent to-transparent opacity-50 pointer-events-none"></div>
          
          {/* Text */}
          <div className="md:w-2/3 text-center md:text-left relative z-10">
            <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-2">
              Muhammad Ahsan Ali
            </h1>
            <p className="text-aseBlue font-medium tracking-wide uppercase text-sm mb-6">Founder & CEO</p>
            <p className="text-gray-300 text-lg md:text-xl leading-relaxed font-light italic border-l-4 border-aseBlue pl-6 ml-4 md:ml-0">
              “At ALLIED SOFTWARE ENGINEERS (PRIVATE) LIMITED, we believe in smart innovation,
              teamwork, and delivering real impact through every line of code.
              Our goal is to inspire the next generation of software excellence
              — built on integrity, creativity, and trust.”
            </p>
          </div>

          {/* Photo */}
          <div className="md:w-1/3 flex justify-center relative z-10">
            <div className="relative group">
              <div className="absolute inset-0 bg-aseBlue rounded-2xl blur-lg opacity-40 group-hover:opacity-70 transition duration-500"></div>
              <img
                src="/team/Ahsan.jpeg"
                alt="Muhammad Ahsan Ali"
                className="w-56 h-56 rounded-2xl object-cover relative z-10 border border-white/10 shadow-2xl group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Vision Section */}
          <motion.div 
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            className="bg-slate-800/40 border border-white/5 rounded-3xl p-8 md:p-10 hover:border-white/10 transition-colors"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-aseBlue/10 flex items-center justify-center text-aseBlue text-xl">👁️</div>
              <h2 className="text-3xl font-display font-bold text-white">Our Vision</h2>
            </div>
            <p className="text-gray-400 leading-relaxed space-y-4">
              <span className="block">
                At <b>ALLIED SOFTWARE ENGINEERS (PRIVATE) LIMITED</b>, our vision is to empower
                innovation through intelligent technology and establish ourselves as
                a global leader in software development, digital transformation, and
                enterprise solutions.
              </span>
              <span className="block mt-4">
                We strive to bridge human creativity with technological excellence,
                crafting digital solutions that inspire trust, optimize performance,
                and transform the way the world works.
              </span>
              <span className="block mt-4">
                Our long-term vision is to build a smarter, more connected, and
                sustainable digital ecosystem — where businesses of every scale
                harness the power of innovation, artificial intelligence, and
                automation to achieve their greatest potential.
              </span>
            </p>
          </motion.div>

          {/* Mission Section */}
          <motion.div 
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            className="bg-slate-800/40 border border-white/5 rounded-3xl p-8 md:p-10 hover:border-white/10 transition-colors"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center text-purple-400 text-xl">🎯</div>
              <h3 className="text-3xl font-display font-bold text-white">Our Mission</h3>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Our mission is to deliver cutting-edge, reliable, and scalable
              software solutions that enable our clients to achieve efficiency,
              growth, and digital excellence.
            </p>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-start gap-3"><span className="text-aseBlue mt-1">✦</span> <span><b>Innovating</b> continuously to create impactful products.</span></li>
              <li className="flex items-start gap-3"><span className="text-aseBlue mt-1">✦</span> <span><b>Empowering</b> businesses through customized technology.</span></li>
              <li className="flex items-start gap-3"><span className="text-aseBlue mt-1">✦</span> <span><b>Building</b> long-term partnerships based on trust.</span></li>
              <li className="flex items-start gap-3"><span className="text-aseBlue mt-1">✦</span> <span><b>Nurturing</b> talent in an environment where engineers thrive.</span></li>
              <li className="flex items-start gap-3"><span className="text-aseBlue mt-1">✦</span> <span><b>Upholding</b> quality, security, and sustainability.</span></li>
            </ul>
          </motion.div>
        </div>

        {/* Values Section */}
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          className="bg-slate-800/40 border border-white/5 rounded-3xl p-8 md:p-10"
        >
          <h3 className="text-2xl font-display font-bold text-center mb-8">Our Core Values</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { title: "Innovation", desc: "Embracing creativity to drive advancement." },
              { title: "Integrity", desc: "Honesty, ethics, and full accountability." },
              { title: "Excellence", desc: "Perfection in every line of code." },
              { title: "Collaboration", desc: "Teamwork creates stronger solutions." },
              { title: "Customer Success", desc: "Measured by the success of our clients." },
              { title: "Sustainability", desc: "Responsible, efficient, and future-ready." },
              { title: "Empowerment", desc: "Think boldly, act decisively, grow continuously." }
            ].map((value, idx) => (
              <div key={idx} className="p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-aseBlue/30 transition-colors">
                <h4 className="font-semibold text-white mb-2">{value.title}</h4>
                <p className="text-sm text-gray-400">{value.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Closing Line */}
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          className="text-center pb-10"
        >
          <p className="text-2xl font-display font-light text-transparent bg-clip-text bg-gradient-to-r from-aseBlue to-purple-400">
            Together, we build the future — one innovation at a time.
          </p>
        </motion.div>
      </div>
    </main>
  );
}
